import { ChevronLeft } from "@tamagui/lucide-icons";
import { useAuth } from "common";
import { FormikProvider, useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Button, Separator, Text, View, XStack, YStack } from "tamagui";
import { GradientButton } from "tamagui-config";

import { FormikInput } from "../components/FormikInput";
import { GlowCircle } from "../components/GlowCircle";
import useFieldValidationSchema from "../hooks/useFieldValidationSchema";
import { useShowToast } from "../hooks/useShowToast";

type ForgotPasswordFormValues = {
  email: string;
};

const ForgotPassword = () => {
  const { forgotPassword, isLoading } = useAuth();
  const { forgotPasswordValidationSchema } = useFieldValidationSchema();
  const navigate = useNavigate();
  const { showErrorToast } = useShowToast();
  const { t } = useTranslation();

  const onSubmit = async ({ email }: ForgotPasswordFormValues) => {
    try {
      await forgotPassword(email);
      navigate("/forgot-password-confirmation", { state: { email } });
    } catch (error) {
      showErrorToast(
        typeof error === "object" && "message" in (error as any)
          ? (error as any).message
          : error,
      );
    }
  };

  const formik = useFormik<ForgotPasswordFormValues>({
    initialValues: {
      email: "",
    },
    enableReinitialize: true,
    validationSchema: forgotPasswordValidationSchema,
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit,
  });

  return (
    <FormikProvider value={formik}>
      <form onSubmit={formik.handleSubmit}>
        <View
          position="relative"
          minHeight="100vh"
          alignItems="center"
          justifyContent="center"
          backgroundColor="black"
        >
          <View position="absolute" width="100%" height="100%">
            <GlowCircle />
          </View>

          <YStack
            gap="$6"
            width={473}
            maxWidth="100%"
            padding="$7"
            backgroundColor="$transparentBorder"
            borderWidth={2}
            borderColor="$transparentBorder"
            borderRadius="$8"
          >
            <XStack gap="$2" alignItems="center">
              <View width={56}>
                <Button
                  size={56}
                  backgroundColor="$transparentBorder"
                  borderWidth={2}
                  borderColor="$transparentBorder"
                  borderRadius="50%"
                  onPress={(event) => {
                    event.preventDefault();
                    navigate(-1);
                  }}
                >
                  <ChevronLeft />
                </Button>
              </View>
              <View flex={1} alignItems="center">
                <Text fontWeight="bold" color="$white12">
                  {t("forgotPassword.title")}
                </Text>
              </View>
              <View width={56} />
            </XStack>

            <Separator />

            <YStack gap="$3.5" padding="$3.5">
              <YStack gap="$2.5" paddingHorizontal="$5" paddingBottom="$3.5">
                <Text
                  fontSize="$7.5"
                  fontWeight="bold"
                  color="$white12"
                  textAlign="center"
                >
                  {t("forgotPassword.formTitle")}
                </Text>
                <Text color="$white10" textAlign="center">
                  {t("forgotPassword.formDescription")}
                </Text>
              </YStack>

              <FormikInput
                fieldName="email"
                placeholder={t("forgotPassword.emailPlaceholder")}
                autoComplete="email"
                autoCapitalize="none"
                keyboardType="email-address"
              />

              <GradientButton
                rounded
                width="100%"
                loading={isLoading}
                onPress={() => formik.handleSubmit()}
              >
                {t("forgotPassword.submitButton")}
              </GradientButton>
            </YStack>
          </YStack>
        </View>
      </form>
    </FormikProvider>
  );
};

export default ForgotPassword;
